import {
    useCallback, useMemo,
} from 'react'
import Grid from '@mui/material/Grid'
import isEmpty from 'lodash/isEmpty'

import AdvancedList, {
    ListColumn,
} from 'app/shared-components/AdvancedList/AdvancedList'
import useEnumValues from 'app/hooks/useEnumValues'
import Select from 'app/shared-components/Select/SelectContainer'
import InputSingleline from 'app/shared-components/InputSingleline'
import useLabel from 'app/hooks/useLabel'

import {
    ContainerAvailabilityAllowedProductTypes,
} from 'app/enums/label.enums'
import {
    ProductMetricsType,
} from '../../serviceCenters.types'
import useStyles from './ProductMetrics.style'

const defaultValue = {
    productType: undefined,
    safeMinimumPreconStock: undefined,
    safeMinimumUsableStock: undefined,
    processingRatio: undefined,
}

const getColumns = (l: (l: string) => string): ListColumn[] => {
    return [
        {
            colSpan: 6,
            label: l('ProductMetrics.type'),
        },
        {
            colSpan: 'grow',
            label: l('ProductMetrics.safeMinimumPreconStock'),
            align: 'right',
        },
        {
            colSpan: 'grow',
            label: l('ProductMetrics.safeMinimumUsableStock'),
            align: 'right',
        },
        {
            colSpan: 'grow',
            label: l('ProductMetrics.processingRatio'),
            align: 'right',
        },
    ]
}

const defaultProps = {
    disabled: false,
    required: false,
    exists: false,
    value: undefined,
}

const ProductMetrics = ({
    value,
    onChange,
    disabled,
    required,
    exists,
}: {
    value?: ProductMetricsType[],
    onChange: (val: ProductMetricsType) => void,
    disabled?: boolean,
    required?: boolean,
    exists?: boolean,
}) => {
    const {
        classes,
    } = useStyles()
    const allowedValues = useEnumValues()(ContainerAvailabilityAllowedProductTypes)

    const l = useLabel()

    const columns = useMemo(() => {
        return getColumns(l)
    }, [l])

    const onItemCellChange = useCallback(({
        item, newValue, index, handleChange,
    }) => {
        const newItem = {
            ...item,
            ...newValue,
        }

        handleChange(newItem, index)
    }, [])

    const validateItemForAdd = useCallback((item: ProductMetricsType): boolean => {
        return !isEmpty(item) && Object.values(item).every((el) => {
            return Boolean(el)
        })
    }, [])

    const renderItem = useCallback(({
        index,
        item,
        handleChange,
    }) => {
        return (
            <>
                <Grid
                    key={`row-container-${index}`}
                    size={columns[0].colSpan}
                    p={1}
                >
                    <Select
                        name="containerType"
                        allowedValues={allowedValues}
                        value={item?.productType}
                        onChange={(newValue) => {
                            return onItemCellChange({
                                newValue: {
                                    productType: newValue,
                                },
                                index,
                                item,
                                handleChange,
                            })
                        }}
                        required={required}
                        disabled={disabled}
                    />
                </Grid>
                <Grid
                    key={`row-safeMinimumPreconStock-${index}`}
                    size={columns[1].colSpan}
                    p={1}
                >
                    <InputSingleline
                        name="safeMinimumPreconStock"
                        type="number"
                        min={1}
                        centerAligned
                        onChange={(newValue) => {
                            return onItemCellChange({
                                index,
                                item,
                                handleChange,
                                newValue: {
                                    safeMinimumPreconStock: Number(newValue),
                                },
                            })
                        }}
                        className={classes.input}
                        value={item?.safeMinimumPreconStock}
                        required={required}
                        disabled={disabled}
                    />
                </Grid>
                <Grid
                    key={`row-safeMinimumUsableStock-${index}`}
                    size={columns[2].colSpan}
                    p={1}
                >
                    <InputSingleline
                        name="safeMinimumUsableStock"
                        type="number"
                        min={1}
                        centerAligned
                        onChange={(newValue) => {
                            return onItemCellChange({
                                index,
                                item,
                                handleChange,
                                newValue: {
                                    safeMinimumUsableStock: Number(newValue),
                                },
                            })
                        }}
                        className={classes.input}
                        classNames={{
                            text: item.usableContainersAmount < item.safeMinimumUsableStock
                                ? classes.red : classes.green,
                        }}
                        value={item?.safeMinimumUsableStock}
                        required={required}
                        disabled={disabled}
                    />
                </Grid>
                <Grid
                    key={`row-processingRatio-${index}`}
                    size={columns[2].colSpan}
                    p={1}
                >
                    <InputSingleline
                        name="processingRatio"
                        type="number"
                        min={1}
                        centerAligned
                        onChange={(newValue) => {
                            return onItemCellChange({
                                index,
                                item,
                                handleChange,
                                newValue: {
                                    processingRatio: Number(newValue),
                                },
                            })
                        }}
                        className={classes.input}
                        value={item?.processingRatio}
                        required={required}
                        disabled={disabled}
                    />
                </Grid>
            </>
        )
    }, [
        columns,
        allowedValues,
        required,
        disabled,
        classes,
        onItemCellChange,
    ])

    if (exists) {
        return (
            <>
                {value && (
                    <AdvancedList<ProductMetricsType>
                        onChange={onChange}
                        defaultValue={defaultValue}
                        disabled={disabled}
                        value={value}
                        renderItem={renderItem}
                        addButtonLabel="new container"
                        columns={columns}
                        validateForAdd={validateItemForAdd}
                    />
                )}
            </>
        )
    }

    return (
        <AdvancedList<ProductMetricsType>
            onChange={onChange}
            defaultValue={defaultValue}
            disabled={disabled}
            value={value}
            renderItem={renderItem}
            addButtonLabel="new container"
            columns={columns}
            validateForAdd={validateItemForAdd}
        />
    )
}

ProductMetrics.defaultProps = defaultProps

export default ProductMetrics
